import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { focusWindowsApi } from '../api/services';
import type { FocusWindow } from '../types/models';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm

function timeToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

function formatHour(hour: number): string {
  const h = Math.floor(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display} ${ampm}`;
}

const BLOCK_COLOURS = [
  'rgba(108, 155, 255, 0.35)',
  'rgba(129, 199, 132, 0.35)',
  'rgba(255, 183, 77, 0.35)',
  'rgba(206, 147, 216, 0.35)',
  'rgba(77, 208, 225, 0.35)',
];

export default function FocusTimePage() {
  const [windows, setWindows] = useState<FocusWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<FocusWindow> | null>(null);

  const loadWindows = async () => {
    setLoading(true);
    try {
      setWindows(await focusWindowsApi.getAll());
    } catch (err) {
      console.error('Failed to load focus windows', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWindows(); }, []);

  const handleSave = async () => {
    if (!editItem?.dayOfWeek || !editItem?.startTime || !editItem?.endTime) return;
    try {
      if (editItem.id) {
        await focusWindowsApi.update(editItem.id, editItem);
      } else {
        await focusWindowsApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadWindows();
    } catch (err) {
      console.error('Failed to save focus window', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this focus window?')) return;
    try {
      await focusWindowsApi.delete(id);
      loadWindows();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const openNewDialog = () => {
    setEditItem({
      dayOfWeek: 'Monday',
      startTime: '09:00',
      endTime: '11:00',
      label: '',
      isRecurring: true,
    });
    setDialogOpen(true);
  };

  // Calculate total weekly focus hours
  const totalMinutes = windows.reduce((sum, w) => {
    const start = timeToHour(w.startTime);
    const end = timeToHour(w.endTime);
    return sum + (end - start) * 60;
  }, 0);
  const totalHours = Math.round(totalMinutes / 60 * 10) / 10;

  // Group windows by day for the heatmap
  const windowsByDay: Record<string, FocusWindow[]> = {};
  DAYS.forEach(d => { windowsByDay[d] = []; });
  windows.forEach(w => {
    if (windowsByDay[w.dayOfWeek]) {
      windowsByDay[w.dayOfWeek].push(w);
    }
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Focus Time</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewDialog}>
          Add Focus Window
        </Button>
      </Box>

      {/* Capacity Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Weekly Focus Time</Typography>
            <Typography variant="h5">{totalHours}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Focus Windows</Typography>
            <Typography variant="h5">{windows.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Days Covered</Typography>
            <Typography variant="h5">
              {DAYS.filter(d => windowsByDay[d]?.length > 0).length} / 5
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2.5}>
        {/* Weekly Heatmap */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Weekly Focus Grid
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(5, 1fr)', gap: 0, minWidth: 500 }}>
                  {/* Header row */}
                  <Box />
                  {DAYS.map(day => (
                    <Box key={day} sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {day.substring(0, 3)}
                      </Typography>
                    </Box>
                  ))}

                  {/* Hour rows */}
                  {HOURS.map(hour => (
                    <React.Fragment key={hour}>
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatHour(hour)}
                        </Typography>
                      </Box>
                      {DAYS.map(day => {
                        const dayWindows = windowsByDay[day] || [];
                        const activeWindow = dayWindows.find(w => {
                          const start = timeToHour(w.startTime);
                          const end = timeToHour(w.endTime);
                          return hour >= start && hour < end;
                        });
                        const colourIndex = activeWindow
                          ? dayWindows.indexOf(activeWindow) % BLOCK_COLOURS.length
                          : -1;

                        return (
                          <Box
                            key={`${day}-${hour}`}
                            sx={{
                              height: 32,
                              bgcolor: activeWindow ? BLOCK_COLOURS[colourIndex] : 'rgba(255,255,255,0.02)',
                              border: 1,
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background-color 0.2s',
                            }}
                          >
                            {activeWindow && hour === Math.ceil(timeToHour(activeWindow.startTime)) && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'text.primary' }}>
                                {activeWindow.label}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Focus Windows List */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Declared Windows
              </Typography>
              {windows.length === 0 && !loading && (
                <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                  No focus windows declared yet
                </Typography>
              )}
              <Stack spacing={1}>
                {windows.map((w, i) => (
                  <Box
                    key={w.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: BLOCK_COLOURS[i % BLOCK_COLOURS.length],
                      borderLeft: 3,
                      borderColor: 'primary.main',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{w.label || 'Focus Block'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {w.dayOfWeek} {w.startTime.substring(0, 5)} - {w.endTime.substring(0, 5)}
                      </Typography>
                      {w.isRecurring && (
                        <Chip label="Recurring" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => { setEditItem(w); setDialogOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(w.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.id ? 'Edit Focus Window' : 'New Focus Window'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              fullWidth
              value={editItem?.label || ''}
              onChange={e => setEditItem(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g. Deep Work, Strategy, Writing"
            />
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={editItem?.dayOfWeek || 'Monday'}
                label="Day of Week"
                onChange={e => setEditItem(prev => ({ ...prev, dayOfWeek: e.target.value }))}
              >
                {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editItem?.startTime || '09:00'}
                onChange={e => setEditItem(prev => ({ ...prev, startTime: e.target.value }))}
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editItem?.endTime || '11:00'}
                onChange={e => setEditItem(prev => ({ ...prev, endTime: e.target.value }))}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={editItem?.isRecurring ?? true}
                  onChange={e => setEditItem(prev => ({ ...prev, isRecurring: e.target.checked }))}
                />
              }
              label="Recurring weekly"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
